import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { markAllNotificationsAsRead, markNotificationAsRead } from '../../services/notificationService';
import { formatDateTime } from '../../utils/date';

interface Notification {
  id: string;
  title: string;
  message: string;
  url: string;
  read_at: string | null;
  created_at: string;
}

interface NotificationModalProps {
  open: boolean;
  notifications: Notification[];
  onClose: () => void;
  onNotificationRead?: (id: string) => void;
  onAllNotificationsRead?: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  notifications,
  onClose,
  onNotificationRead,
  onAllNotificationsRead,
}) => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loadingMarkAll, setLoadingMarkAll] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [open, onClose]);

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.read_at) {
        await markNotificationAsRead(notification.id);

        onNotificationRead?.(notification.id);
      }

      onClose();

      if (notification.url) {
        const url = new URL(notification.url);

        navigate(`${url.pathname}${url.search}${url.hash}`);
      }
    } catch (error) {
      console.error('Failed to open notification:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setLoadingMarkAll(true);

      await markAllNotificationsAsRead();

      onAllNotificationsRead?.();

      setShowConfirmModal(false);

      setShowError(false);
      setErrorMessage('');
    } catch (error: any) {
      console.error(error);

      const message = error?.response?.data?.message || error?.message || 'Something went wrong';

      setErrorMessage(message);
      setShowError(true);

      setTimeout(() => {
        setShowError(false);
      }, 5000);
    } finally {
      setLoadingMarkAll(false);
    }
  };

  const handleViewMore = () => {
    onClose();
    navigate('/notifications'); // adjust to your actual route path
  };

  if (!open) return null;

  return (
    <div ref={modalRef} className="absolute right-0 top-12 w-96 bg-white border rounded-lg shadow-xl z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-[#0c1a3e]">Notifications</h3>

        {notifications.some((n) => !n.read_at) && (
          <button onClick={() => setShowConfirmModal(true)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            Mark all as read
          </button>
        )}
      </div>

      <div className="max-h-[450px] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No notifications available.</div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition ${!notification.read_at ? 'bg-blue-50' : ''}`}
            >
              <div className="flex justify-between gap-3">
                <div className="flex-1">
                  <p className={`font-medium text-sm ${!notification.read_at ? 'text-gray-900' : 'text-gray-600'}`}>{notification.title}</p>

                  <p className="text-xs text-gray-600 mt-1">{notification.message}</p>

                  <p className="text-[11px] text-gray-400 mt-2">{formatDateTime(notification.created_at)}</p>
                </div>

                {!notification.read_at && <span className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0" />}
              </div>
            </button>
          ))
        )}
      </div>

      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t text-center">
          <button onClick={handleViewMore} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            View more
          </button>
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[350px]">
            <h4 className="font-semibold text-lg mb-2">Mark all notifications as read?</h4>

            <p className="text-sm text-gray-600 mb-4">This will mark every notification as read.</p>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 border rounded">
                Cancel
              </button>

              <button onClick={handleMarkAllRead} disabled={loadingMarkAll} className="px-4 py-2 bg-red-600 text-white rounded">
                {loadingMarkAll ? 'Loading...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showError && (
        <div className="fixed bottom-5 right-5 z-[99999]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg min-w-[350px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4m0 4h.01M10.29 3.86l-8.18 14A2 2 0 003.82 21h16.36a2 2 0 001.71-3.14l-8.18-14a2 2 0 00-3.42 0z"
                    />
                  </svg>
                </div>

                <div>
                  <p className="font-semibold">Error</p>
                  <p className="text-sm break-words">{errorMessage}</p>
                </div>
              </div>

              <button onClick={() => setShowError(false)} className="ml-4 text-white text-xl leading-none">
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationModal;
